// src/features/wf2/popup/W2ProgramarModal.jsx
import React, { useEffect, useMemo } from "react";
import s from "./W2ProgramarModal.module.css";

const FIELD_LABELS = {
  horario_do_intervalo: "Horário do Intervalo (WF2)",
  horario_inicio_jornada_turma: "Horário de Início da Jornada da Turma",
  quantidade_maquinista: "Quantidade de Maquinista",
  maquinista: "Maquinista",
  locomotiva1: "Locomotiva 1",
  locomotiva2: "Locomotiva 2",
  tipo_acionamento: "Tipo de Acionamento",
  tipo_programacao: "Tipo de Programação",
  prioridade: "Prioridade",
  responsavel_pelo_aceite: "Responsável pelo Aceite",
};

/* ===== Helpers locais ===== */
const norm = (v) => (v == null ? "" : String(v));
const trim = (v) => norm(v).trim();

function toHHMM(raw) {
  const sv = trim(raw);
  if (!sv) return "";
  let m = sv.match(/^(\d{1,2}):([0-5]\d)$/);
  if (m) return `${m[1].padStart(2, "0")}:${m[2]}`;
  m = sv.match(/^(\d{1,2}):([0-5]\d):([0-5]\d)$/);
  if (m) return `${m[1].padStart(2, "0")}:${m[2]}`;
  m = sv.match(/^(\d{1,2})([0-5]\d)$/);
  if (m) return `${m[1].padStart(2, "0")}:${m[2]}`;
  m = sv.match(/(\d{1,2}):([0-5]\d)/);
  if (m) return `${m[1].padStart(2, "0")}:${m[2]}`;
  return "";
}

function normalizePrioridade(v) {
  const u = trim(v).toUpperCase();
  if (!u) return "";
  if (/^P[0-5]$/.test(u)) return u;
  if (u === "ALTA") return "P1";
  if (u === "MEDIA" || u === "MÉDIA") return "P3";
  if (u === "BAIXA") return "P5";
  return "";
}

/* Campos obrigatórios para programar */
const REQUIRED_KEYS = [
  "horario_do_intervalo",
  "horario_inicio_jornada_turma",
  "tipo_programacao",
  "prioridade",
  "responsavel_pelo_aceite",
];

function getMissingKeysFromForm(form = {}) {
  const missing = [];

  for (const k of REQUIRED_KEYS) {
    const raw = form?.[k];

    // horários obrigatórios → HH:MM válido
    if (k === "horario_do_intervalo" || k === "horario_inicio_jornada_turma") {
      if (!toHHMM(raw)) missing.push(k);
      continue;
    }

    // prioridade obrigatória → P0..P5
    if (k === "prioridade") {
      if (!normalizePrioridade(raw)) missing.push(k);
      continue;
    }

    // demais obrigatórios → não podem ser vazios
    if (!trim(raw)) missing.push(k);
  }

  return missing;
}

function buildMissingDetails(keys = []) {
  return keys.map((k) => {
    let hint = "";
    if (k === "horario_do_intervalo" || k === "horario_inicio_jornada_turma") {
      hint = "Use o formato HH:MM (ex.: 07:30).";
    } else if (k === "prioridade") {
      hint = "Selecione uma prioridade (P0 a P5).";
    } else if (k === "tipo_programacao") {
      hint = 'Informe se é "CONTRATO" ou "OPORTUNIDADE".';
    } else if (k === "responsavel_pelo_aceite") {
      hint = "Informe o nome de quem está aprovando a programação.";
    }
    return {
      key: k,
      label: FIELD_LABELS[k] || k,
      hint,
    };
  });
}

/**
 * Modal que VALIDA os campos obrigatórios.
 *
 * - Se estiver faltando algo → mostra popup com lista.
 * - Se estiver tudo ok → não mostra popup, só devolve ok=true pro onClose.
 *
 * Props:
 *  - open: boolean
 *  - codigo_unico: string | number (só pra título) [não usado mais no título]
 *  - form: objeto com os campos da ficha (W2FichaModal)
 *  - onClose(ok: boolean, info?: { missingKeys: string[] }): void
 */
export default function W2ProgramarModal({
  open = false,
  codigo_unico = "",
  form = {},
  onClose = () => {},
}) {
  const missingKeys = useMemo(() => getMissingKeysFromForm(form), [form]);
  const missingDetails = useMemo(
    () => buildMissingDetails(missingKeys),
    [missingKeys]
  );
  const hasMissing = missingDetails.length > 0;

  // Se não tem nada faltando → avisa o pai e não mostra popup
  useEffect(() => {
    if (!open) return;
    if (!hasMissing) {
      onClose(true, { missingKeys: [] });
    }
  }, [open, hasMissing, onClose]);

  if (!open) return null;

  // Se está tudo ok, não renderiza nada (efeito acima já avisou o pai)
  if (!hasMissing) return null;

  // Se chegou aqui, tem campos obrigatórios pendentes → mostra popup
  return (
    <div className={s.justBack}>
      <div className={s.justBox}>
        <div className={s.justTitle}>
          {/* Título sem #codigo_unico */}
          Para programar, preencha os campos:
        </div>

        <div className={s.justMsg}>
          <strong>Campos obrigatórios pendentes:</strong>
          <ul>
            {missingDetails.map(({ key, label, hint }) => (
              <li key={key}>
                <span className={s.fieldLabel}>{label}</span>
                {hint && <div className={s.fieldHint}>{hint}</div>}
              </li>
            ))}
          </ul>
        </div>

        <div className={s.justActions}>
          <button
            type="button"
            className={`${s.btn} ${s.btnPrimary}`}
            onClick={() => onClose(false, { missingKeys })}
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
}
